<template>
  <p :class="lsLinkType === 'filter' ? 'mt-2 text--small' : ''">
    {{ lsHelpText }}
    <br />
    <LinkElement
      class="mt-2"
      :style-type="lsLinkType === 'list' ? 'attraction-secondary' : 'default'"
      size="medium"
      link=""
      icon-type="icon-left"
      icon-name="focal-length"
      @click="
        (showModal = true),
          openModal(),
          pushAnalyticsEvent(
            'open_ls_in_modal_' + gaEventCategory,
            'Open Modal'
          )
      "
      >{{
        $textDictionary['productsList.ls.button'] ?? 'Calculate focal length'
      }}</LinkElement
    >
  </p>

  <ModalModule
    v-model="showModal"
    :title="
      $textDictionary['productsList.ls.modal.headline'] ?? 'Lens Selector'
    "
    close-icon-text="close"
    :is-full-screen="true"
    :esc-to-close="true"
    :click-to-close="true"
    @close="
      (showModal = false),
        removeHash(),
        pushAnalyticsEvent('close_ls_in_modal', 'Close Modal')
    "
    @abort="
      (showModal = false),
        removeHash(),
        pushAnalyticsEvent('close_ls_in_modal', 'Close Modal')
    "
  >
    <LibExternalTool :item="tool" mode="modal"> </LibExternalTool>

    <div class="row sticky">
      <Button
        class="col-lg-3"
        style-type="attraction"
        icon-type="icon-left"
        icon-name="focal-length"
        :disabled="disableButton"
        @click="
          (showModal = false),
            setFocalLength(),
            pushAnalyticsEvent(
              'apply_values_ls_in_modal',
              'Apply LS values Modal'
            )
        "
        >{{
          $textDictionary['productsList.ls.modal.button'] ??
          'Apply calculated values'
        }}</Button
      >
    </div>
  </ModalModule>
</template>

<script setup lang="ts">
import {
  ModalModule,
  LinkElement,
  Button
} from '@hypercodestudio/basler-components';
import type { ITechnicalComponent } from '~/lib/ContentfulService';
import { TYPE } from 'vue-toastification';

const { $textDictionary, $analytics } = useNuxtApp();
const toasts = useShowToast();
const props = defineProps<Props>();
const router = useRouter();

interface Props {
  tool: ITechnicalComponent;
  lsLinkType: string;
  gaEventCategory: string;
}

const emits = defineEmits<{
  focalLength: [minValue: string, maxValue: string];
  closeModal: [orgHash: string];
}>();

const showModal = ref(false);
const disableButton = ref(true);
const orgHash = ref();

const tool = props.tool;

const lsHelpText =
  props.lsLinkType === 'filter'
    ? $textDictionary.value['productsList.ls.button.helptext.filter'] ??
      'Calculate focal length?'
    : $textDictionary.value['productsList.ls.button.helptext'] ??
      'Need help to calculate focal length?';

declare global {
  interface Window {
    eguidanceAPI: {
      lensSelector: any;
    };
  }
}

const apiResponse = ref(window?.eguidanceAPI?.lensSelector?.calculationResult);
const apiData = (e: Event): void => {
  apiResponse.value = (e as CustomEvent).detail;

  if (
    apiResponse.value &&
    apiResponse.value.focal_length_range &&
    apiResponse.value.focal_length_range.min &&
    apiResponse.value.focal_length_range.max
  ) {
    disableButton.value = false;
  }
  if (apiResponse.value.error) {
    disableButton.value = true;
  }
};

onMounted(() => {
  window.addEventListener('lensSelectorValueChanged', apiData);
});

onUnmounted(() =>
  window.removeEventListener('lensSelectorValueChanged', apiData)
);

function openModal() {
  orgHash.value = router.currentRoute.value.hash;
}

function removeHash() {
  router.push({ hash: orgHash.value, force: true });
}

function pushAnalyticsEvent(action: string, label: string) {
  $analytics.pushToDataLayer({
    action: action,
    category: 'filter_link',
    event: 'click_Internal',
    label: label
  });
}

function setFocalLength() {
  const min = apiResponse.value.focal_length_range.min;
  const max = apiResponse.value.focal_length_range.max;
  if (min && max) {
    emits('focalLength', min, max);
    const message =
      $textDictionary.value['productsList.ls.modal.success'] ??
      'Data applied successfully';

    toasts.showToast(message, {
      type: TYPE.SUCCESS
    });
  }
}
</script>

<style>
.modal__content {
  position: relative;
  padding-bottom: 40px;
}
.sticky {
  position: fixed;
  width: 100%;
  max-width: 1304px;
  margin: 0 auto;
  bottom: 60px;
  text-align: right;
}
</style>
